import Phaser from 'phaser';
import {
  COLORS,
  DEPTHS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  SHORT_PAY_MODAL,
  toHexColor,
} from '../config/gameConfig';
import { COPY } from '../content/copy';
import type { EventBus, ShortPayChoice } from '../systems/EventBus';
import { renderHelpLink } from './HelpLink';
import { propagateDepth } from './propagateDepth';

const WIRE_ID = 'W-3114';
const WIRE_CUSTOMER = 'TechStart';
const PAID_AMOUNT = 4200;
const INVOICE_AMOUNT = 5000;
const INVOICE_ID = 'inv-techstart-001';

type ChoiceKey = 'applyAndChase' | 'creditMemo' | 'reject';

const CHOICE_ORDER: readonly { key: ChoiceKey; id: ShortPayChoice }[] = [
  { key: 'applyAndChase', id: 'apply-and-chase' },
  { key: 'creditMemo', id: 'credit-memo' },
  { key: 'reject', id: 'reject' },
];

export class ShortPayModal {
  private container: Phaser.GameObjects.Container | null = null;
  private escHandler: ((ev: KeyboardEvent) => void) | null = null;

  private readonly onBeat = (p: { beatId: string }) => {
    if (p.beatId === 'beat-4-short-pay' && !this.container) this.open();
  };

  constructor(private readonly scene: Phaser.Scene, private readonly bus: EventBus) {
    bus.on('beat:triggered', this.onBeat);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  chooseForTest(choice: ShortPayChoice): void {
    this.emitResolved(choice);
    this.close();
  }

  private open(): void {
    this.bus.emit('term:unlocked', { term: 'short-pay' });
    this.bus.emit('short-pay:opened', { wireId: WIRE_ID, customer: WIRE_CUSTOMER });

    const x = Math.round((GAME_WIDTH - SHORT_PAY_MODAL.width) / 2);
    const y = Math.round((GAME_HEIGHT - SHORT_PAY_MODAL.height) / 2);

    const root = this.scene.add.container(0, 0);
    root.setDepth(DEPTHS.modal);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.black, SHORT_PAY_MODAL.backdropAlpha)
      .setOrigin(0, 0);
    root.add(backdrop);

    const panel = this.scene.add
      .rectangle(x, y, SHORT_PAY_MODAL.width, SHORT_PAY_MODAL.height, COLORS.windowBody)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive();
    root.add(panel);

    const title = this.scene.add.text(
      x + SHORT_PAY_MODAL.paddingX,
      y + SHORT_PAY_MODAL.paddingY,
      COPY.shortPay.title,
      {
        fontFamily: FONTS.ui,
        fontSize: SHORT_PAY_MODAL.titleFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    root.add(title);

    renderHelpLink(
      this.scene,
      root,
      x + SHORT_PAY_MODAL.width - SHORT_PAY_MODAL.paddingX,
      y + SHORT_PAY_MODAL.paddingY + 4,
      'short-pay',
      this.bus,
    );

    const wireY = y + SHORT_PAY_MODAL.paddingY + title.height + 6;
    const wire = this.scene.add.text(
      x + SHORT_PAY_MODAL.paddingX,
      wireY,
      COPY.shortPay.wireSummary,
      {
        fontFamily: FONTS.ui,
        fontSize: SHORT_PAY_MODAL.wireSummaryFontSize,
        color: toHexColor(COLORS.accentWarn),
        fontStyle: 'bold',
      },
    );
    root.add(wire);

    const subpromptY = wireY + wire.height + 10;
    const subprompt = this.scene.add.text(
      x + SHORT_PAY_MODAL.paddingX,
      subpromptY,
      COPY.shortPay.subprompt,
      {
        fontFamily: FONTS.ui,
        fontSize: SHORT_PAY_MODAL.subpromptFontSize,
        color: toHexColor(COLORS.textPrimary),
      },
    );
    root.add(subprompt);

    const rowWidth = SHORT_PAY_MODAL.width - SHORT_PAY_MODAL.paddingX * 2;
    let rowY = subpromptY + subprompt.height + 10;

    for (const choice of CHOICE_ORDER) {
      const copy = COPY.shortPay.choices[choice.key];
      const row = this.scene.add
        .rectangle(
          x + SHORT_PAY_MODAL.paddingX,
          rowY,
          rowWidth,
          SHORT_PAY_MODAL.choiceRowHeight,
          COLORS.windowChrome,
        )
        .setOrigin(0, 0)
        .setStrokeStyle(1, COLORS.windowBorder)
        .setInteractive({ useHandCursor: true });
      root.add(row);

      const label = this.scene.add.text(
        x + SHORT_PAY_MODAL.paddingX + 12,
        rowY + 10,
        copy.label,
        {
          fontFamily: FONTS.ui,
          fontSize: SHORT_PAY_MODAL.choiceLabelFontSize,
          color: toHexColor(COLORS.textPrimary),
          fontStyle: 'bold',
        },
      );
      root.add(label);

      const blurb = this.scene.add.text(
        x + SHORT_PAY_MODAL.paddingX + 12,
        rowY + 10 + label.height + 4,
        copy.blurb,
        {
          fontFamily: FONTS.ui,
          fontSize: SHORT_PAY_MODAL.choiceBlurbFontSize,
          color: toHexColor(COLORS.textMuted),
          wordWrap: { width: rowWidth - 24 },
        },
      );
      root.add(blurb);

      row.on('pointerdown', () => {
        this.emitResolved(choice.id);
        this.showToast();
        this.close();
      });

      rowY += SHORT_PAY_MODAL.choiceRowHeight + SHORT_PAY_MODAL.choiceRowGap;
    }

    const cancel = this.scene.add
      .text(
        x + SHORT_PAY_MODAL.width - SHORT_PAY_MODAL.paddingX,
        y + SHORT_PAY_MODAL.height - SHORT_PAY_MODAL.paddingY,
        COPY.shortPay.cancelButton,
        {
          fontFamily: FONTS.ui,
          fontSize: SHORT_PAY_MODAL.cancelButtonFontSize,
          color: toHexColor(COLORS.textMuted),
        },
      )
      .setOrigin(1, 1)
      .setInteractive({ useHandCursor: true });
    cancel.on('pointerdown', () => this.close());
    root.add(cancel);

    this.escHandler = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') this.close();
    };
    this.scene.input.keyboard?.on('keydown', this.escHandler);

    this.container = root;
    propagateDepth(root, DEPTHS.modal);
  }

  private emitResolved(choice: ShortPayChoice): void {
    this.bus.emit('short-pay:resolved', {
      wireId: WIRE_ID,
      customer: WIRE_CUSTOMER,
      paidAmount: PAID_AMOUNT,
      invoiceAmount: INVOICE_AMOUNT,
      invoiceId: INVOICE_ID,
      choice,
    });
  }

  private showToast(): void {
    const toast = this.scene.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 80,
        `${COPY.shortPay.sentToastPrefix} TechStart`,
        {
          fontFamily: FONTS.ui,
          fontSize: SHORT_PAY_MODAL.toastFontSize,
          color: toHexColor(COLORS.textPrimary),
          backgroundColor: toHexColor(COLORS.windowChrome),
          padding: { x: 12, y: 8 },
        },
      )
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTHS.modal);

    this.scene.time.delayedCall(SHORT_PAY_MODAL.toastDurationMs, () => toast.destroy());
  }

  private close(): void {
    if (this.escHandler) {
      this.scene.input.keyboard?.off('keydown', this.escHandler);
      this.escHandler = null;
    }
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }

  private teardown(): void {
    this.bus.off('beat:triggered', this.onBeat);
    this.close();
  }
}
